import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
//import NotificationDropdown from "./NotificationDropdown";
import logo from "../assets/Dreamframe logo black background.png";

const TopBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [, setShowNotifications] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("/user.png");

  const [accountBannerVisible, setAccountBannerVisible] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(true);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) alert("Logout failed: " + error.message);
    else navigate("/login");
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load avatar
  useEffect(() => {
    const fetchAvatar = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const { data, error } = await supabase
        .from("creator_profiles")
        .select("profile_image_url")
        .eq("user_id", session.user.id)
        .single();
      if (!error && data?.profile_image_url) setAvatarUrl(data.profile_image_url);
    };
    fetchAvatar();
  }, []);

  // Account Info completion check (controls the banner)
  useEffect(() => {
    let live = true;
    (async () => {
      setCheckingAccount(true);

      const dismissed =
        localStorage.getItem("df_accountinfo_dismissed") === "1";
      if (dismissed) {
        if (live) {
          setAccountBannerVisible(false);
          setCheckingAccount(false);
        }
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        if (live) {
          setAccountBannerVisible(false);
          setCheckingAccount(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("account_info")
        .select("is_complete")
        .eq("user_id", userId)
        .maybeSingle();

      if (!live) return;

      if (error) {
        setAccountBannerVisible(false);
      } else {
        const complete = data?.is_complete === true;
        setAccountBannerVisible(!complete);
      }
      setCheckingAccount(false);
    })();
    return () => {
      live = false;
    };
  }, []);

  // 🔔 Listen for AccountInfo completion event so we can hide the banner immediately
  useEffect(() => {
    const handleAccountInfoComplete = () => {
      try {
        localStorage.setItem("df_accountinfo_dismissed", "1");
      } catch (_) {
        // ignore storage errors
      }
      setAccountBannerVisible(false);
    };

    window.addEventListener(
      "df-accountinfo-complete",
      handleAccountInfoComplete
    );
    return () => {
      window.removeEventListener(
        "df-accountinfo-complete",
        handleAccountInfoComplete
      );
    };
  }, []);

  return (
    <>
      {/* Top bar */}
      <div className="fixed inset-x-0 top-0 z-50 flex h-16 items-center justify-between border-b border-white/10 bg-gradient-to-r from-[#1F1F1F] to-[#111827] px-4 md:px-6 text-slate-100 shadow-[0_0_18px_rgba(127,0,255,0.35)]">
        {/* Logo + Title */}
        <div className="flex items-center gap-3 -ml-1">
          <img
            src={logo}
            alt="Dreamframe Logo"
            className="h-12 w-12 rounded-md object-cover shadow-[0_0_10px_rgba(127,0,255,0.6)]"
          />
          <div className="text-sm font-semibold text-cyan-300 md:text-base">
            Dreamframe Studio – Collab Hub (Lite)
          </div>
        </div>

        {/* Right side: notifications + avatar */}
        <div
          ref={menuRef}
          className="relative flex items-center gap-5 text-slate-100"
        >
          {/* Notifications Hidden till Collaboration functionality enables */}

          {/* Avatar + menu */}
          <div className="relative">
            <button
              type="button"
              onClick={toggleMenu}
              className="inline-flex items-center justify-center"
            >
              <img
                src={avatarUrl}
                alt="User"
                className="h-10 w-10 rounded-full border-2 border-fuchsia-500/60 object-cover shadow-[0_0_10px_rgba(217,70,239,0.7)]"
              />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-11 w-48 rounded-xl border border-white/10 bg-slate-900/95 text-sm text-slate-100 shadow-xl backdrop-blur">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/settings")}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-slate-800/80"
                >
                  <span>⚙️</span>
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/support")}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-slate-800/80"
                >
                  <span>🙋</span>
                  <span>Support Request</span>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-slate-800/80"
                >
                  <span>🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Info completion banner (under top bar) */}
      {accountBannerVisible && !checkingAccount && (
        <div className="mt-16 flex items-center justify-between gap-3 border-b border-purple-500/50 bg-purple-900/30 px-4 py-2.5 text-[13px] text-slate-100 backdrop-blur">
          <div className="leading-snug">
            ⚠️ Complete your <strong>Account Info</strong> to enable payouts and
            faster support.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings/account")}
              className="rounded-lg bg-gradient-to-r from-fuchsia-500 to-indigo-500 px-3 py-1.5 text-xs font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110"
            >
              Complete Now →
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
